import { FormEvent, useEffect, useState } from "react";
import {
  KeyRound,
  Loader2,
  Lock,
  LogIn,
  ShieldAlert,
  User,
} from "lucide-react";
import { useLogin } from "../lib/auth";

// Demo quick-fill roster — intentionally EMPTY in this distribution so that no
// real account credentials or employee identities ship in the bundle. Populate
// at your own site if you want a click-to-fill sign-in helper: each account
// shares one demo password (set DEMO_PASSWORD) and the login form auto-appends
// @company.com to a bare employee id. Left empty, the quick-fill panel is simply
// hidden and users type their own credentials.
type DemoAccount = { id: string; name: string; title: string };
const DEMO_PASSWORD = "P@ssw0rd";
const DEMO_ACCOUNTS: { role: string; tone: string; rows: DemoAccount[] }[] = [
  {
    role: "Admin",
    tone: "admin",
    rows: [
      { id: "EMP-5401879", name: "นายเอก ยอดเซลล์", title: "ผู้ดูแลระบบ" },
    ],
  },
];

// Full-screen login wall shown by RootLayout when there is no UMS session.
// Two columns: a POC credential reference on the left, the branded sign-in
// form on the right. Credentials are exchanged at the BFF for an httpOnly JWT
// cookie, so nothing sensitive lives in the SPA.
export function LoginPage() {
  const login = useLogin();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  // Live UMS names keyed by employee id — overlays the static roster below so a
  // rename in UMS shows here too. Failure leaves the built-in fallback names.
  const [umsNames, setUmsNames] = useState<Record<string, string>>({});

  useEffect(() => {
    let alive = true;
    fetch("/api/portal/auth/directory", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (alive && j?.names) setUmsNames(j.names as Record<string, string>);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password || login.isPending) return;
    login.mutate({ username: username.trim(), password });
  };

  const fill = (id: string) => {
    setUsername(id);
    setPassword(DEMO_PASSWORD);
  };

  return (
    <div className="login-screen">
      <div className="login-layout">
        {/* ── Left: POC credential reference ──────────────────────────────── */}
        <aside className="login-creds panel">
          <div className="login-creds-head">
            <KeyRound size={18} />
            <div>
              <h2>บัญชีทดสอบสำหรับ POC</h2>
              <p className="muted small">
                คลิกที่รายชื่อเพื่อกรอกอัตโนมัติ — รหัสผ่านทุกบัญชีคือ{" "}
                <code>{DEMO_PASSWORD}</code>
              </p>
            </div>
          </div>

          {DEMO_ACCOUNTS.map((group) => (
            <div key={group.role} className="login-creds-group">
              <div className="login-creds-role">
                <span className={`role-chip role-${group.tone}`}>
                  {group.role}
                </span>
              </div>
              <table className="login-creds-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>ชื่อ-นามสกุล / ตำแหน่ง</th>
                    <th>Password</th>
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map((acc) => (
                    <tr
                      key={acc.id}
                      onClick={() => fill(acc.id)}
                      title="คลิกเพื่อกรอกในช่องเข้าสู่ระบบ"
                    >
                      <td>
                        <code>{acc.id}</code>
                      </td>
                      <td>
                        <div className="cred-name">
                          {umsNames[acc.id] ?? acc.name}
                        </div>
                        <div className="cred-title muted small">
                          {acc.title}
                        </div>
                      </td>
                      <td>
                        <code>{DEMO_PASSWORD}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          <p className="login-creds-foot muted small">
            สิทธิ์ตามบทบาท: <b>Admin</b> เห็นทุกเมนู · <b>Maker</b> ไม่เห็น
            Dashboard, Transactions, API Docs · <b>Checker &amp; Approver</b>{" "}
            ไม่เห็น Clients, Workflows, Channels, API Docs
          </p>
        </aside>

        {/* ── Right: sign-in form ─────────────────────────────────────────── */}
        <form className="login-card panel" onSubmit={onSubmit}>
          <div className="login-brand">
            <img
              src="/blockfint_logo.svg"
              alt="Blockfint"
              className="login-logo"
            />
            <div className="login-titles">
              <h1>
                <b>Noti Portal</b>
              </h1>
              {/*<p className="muted small">ระบบบริหารจัดการสินเชื่อ · ธ.ก.ส.</p>*/}
            </div>
          </div>

          <label className="login-field">
            <span>ชื่อผู้ใช้ (Username)</span>
            <div className="login-input">
              <User size={16} />
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
                autoComplete="username"
                placeholder="เช่น 5804588"
                spellCheck={false}
              />
            </div>
            <small className="login-hint">
              รหัสพนักงานหรืออีเมล — ถ้าใส่แค่รหัส ระบบจะเติม{" "}
              <code>@company.com</code> ให้อัตโนมัติ
            </small>
          </label>

          <label className="login-field">
            <span>รหัสผ่าน (Password)</span>
            <div className="login-input">
              <Lock size={16} />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                placeholder="••••••••"
              />
            </div>
          </label>

          {login.isError && (
            <div className="login-error" role="alert">
              <ShieldAlert size={15} />
              <span>
                {(login.error as Error)?.message ?? "เข้าสู่ระบบไม่สำเร็จ"}
              </span>
            </div>
          )}

          <button
            className="btn-primary login-submit"
            type="submit"
            disabled={login.isPending}
          >
            {login.isPending ? (
              <>
                <Loader2 size={16} className="spin" /> กำลังเข้าสู่ระบบ…
              </>
            ) : (
              <>
                <LogIn size={16} /> เข้าสู่ระบบ
              </>
            )}
          </button>

          <p className="login-foot muted small">
            ยืนยันตัวตนผ่าน UMS (ระบบยืนยันตัวตนพนักงาน ธ.ก.ส.) · POC
          </p>
        </form>
      </div>
    </div>
  );
}
